import {
  Button,
  type ColumnDef,
  ErrorState,
  Menu,
  MenuContent,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuTrigger,
  RelatedList,
  Spinner,
  Switch,
} from '@olytic/foundation';
import { RevenuePlanGrid } from '@olytic/foundation/cpq';
import { Layers } from 'lucide-react';
import { useEffect, useId, useState } from 'react';

import { formatCurrency, formatDate, formatMoney, formatNumber, humanize } from '../objects/format';
import { recordHref } from '../objects/routes';
import {
  type CellRecord,
  loadCell,
  loadPlan,
  type PlanCell,
  type PlanGroup,
  type RevenuePlan,
} from './opportunityPageApi';
import { monthLabel, monthLong, planSections } from './pageModel';

const GROUPS: [PlanGroup, string][] = [['line', 'Quote Line'], ['component', 'Component'], ['stream', 'Revenue Stream']];

const revenueColumns: ColumnDef<CellRecord>[] = [
  { key: 'line_name', label: 'Quote Line', primary: true },
  { key: 'period_start', label: 'Start', render: (r) => formatDate(r.period_start) },
  { key: 'period_end', label: 'End', render: (r) => formatDate(r.period_end) },
  { key: 'amount', label: 'Amount', align: 'right', figures: true, render: (r) => formatCurrency(r.amount) },
  { key: 'hours', label: 'Hours', align: 'right', figures: true, render: (r) => (r.hours === null || r.hours === undefined ? '' : formatNumber(r.hours, 1)) },
];

const billingColumns: ColumnDef<CellRecord>[] = [
  { key: 'line_name', label: 'Quote Line', primary: true },
  { key: 'label', label: 'Billing' },
  { key: 'planned_date', label: 'Invoice Date', render: (r) => formatDate(r.planned_date) },
  { key: 'status', label: 'Status', render: (r) => humanize(r.status) },
  { key: 'amount', label: 'Amount', align: 'right', figures: true, render: (r) => formatMoney(r.amount) },
];

/**
 * The Revenue tab: the deal's plan month by month (earned revenue by quote
 * line, component or stream), what is invoiced and deferred, and the hours.
 * A cell opens the schedule or billing rows it is made of.
 */
export function RevenuePlanTab({
  opportunityId,
  onEditSchedules,
  onNavigate,
}: {
  opportunityId: string;
  onEditSchedules?: (() => void) | undefined;
  onNavigate: (href: string) => void;
}): JSX.Element {
  const [group, setGroup] = useState<PlanGroup>('line');
  const [weighted, setWeighted] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [plan, setPlan] = useState<{ state: 'loading' } | { state: 'error'; message: string } | { state: 'ready'; plan: RevenuePlan }>({ state: 'loading' });
  const [selected, setSelected] = useState<{ row: string; month: string } | null>(null);
  const [cell, setCell] = useState<{ state: 'loading' } | { state: 'error'; message: string } | { state: 'ready'; cell: PlanCell } | null>(null);
  const weightedId = useId();

  useEffect(() => {
    let live = true;
    setPlan({ state: 'loading' });
    setSelected(null);
    setCell(null);
    loadPlan(opportunityId, group, weighted)
      .then((p) => { if (live) setPlan({ state: 'ready', plan: p }); })
      .catch((e: unknown) => { if (live) setPlan({ state: 'error', message: e instanceof Error ? e.message : 'The plan could not be loaded.' }); });
    return () => { live = false; };
  }, [opportunityId, group, weighted, attempt]);

  useEffect(() => {
    if (!selected) return;
    let live = true;
    setCell({ state: 'loading' });
    loadCell(opportunityId, selected.row, selected.month)
      .then((c) => { if (live) setCell({ state: 'ready', cell: c }); })
      .catch((e: unknown) => { if (live) setCell({ state: 'error', message: e instanceof Error ? e.message : 'Those records could not be loaded.' }); });
    return () => { live = false; };
  }, [opportunityId, selected]);

  if (plan.state === 'loading') return <Spinner label="Loading the revenue plan…" />;
  if (plan.state === 'error') return <ErrorState title="Could not load the revenue plan" description={plan.message} onRetry={() => setAttempt((n) => n + 1)} />;

  const { months } = plan.plan;
  const rowLabel = (key: string) =>
    plan.plan.rows.find((r) => r.key === key)?.label
    ?? planSections(plan.plan).flatMap((s) => s.rows).find((r) => r.key === key)?.label
    ?? key;

  const toolbar = (
    <div className="flex items-center gap-3">
      <span className="flex items-center gap-2 text-sm">
        <Switch id={weightedId} size="sm" checked={weighted} onCheckedChange={setWeighted} />
        <label htmlFor={weightedId}>Weighted</label>
      </span>
      <Menu>
        <MenuTrigger asChild>
          <Button size="icon-sm" variant="neutral" aria-label={`Group by: ${GROUPS.find(([k]) => k === group)?.[1]}`} title="Group by">
            <Layers aria-hidden className="size-4" />
          </Button>
        </MenuTrigger>
        <MenuContent align="end" className="min-w-48">
          <MenuLabel>Group by</MenuLabel>
          <MenuRadioGroup value={group} onValueChange={(g) => setGroup(g as PlanGroup)}>
            {GROUPS.map(([key, label]) => <MenuRadioItem key={key} value={key}>{label}</MenuRadioItem>)}
          </MenuRadioGroup>
        </MenuContent>
      </Menu>
      {onEditSchedules ? (
        <Button variant="neutral" size="sm" onClick={onEditSchedules}>
          Edit Schedules
        </Button>
      ) : null}
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      <RevenuePlanGrid
        months={months.map((m, i) => ({ key: m, label: monthLabel(m, i === 0), title: monthLong(m) }))}
        sections={planSections(plan.plan)}
        chartSection="plan"
        chartLabel={weighted ? 'Weighted Revenue by Month' : 'Earned Revenue by Month'}
        toolbar={toolbar}
        selected={selected}
        onCellClick={(row, month) => setSelected((s) => (s?.row === row && s.month === month ? null : { row, month }))}
      />
      {selected && cell ? (
        cell.state === 'loading' ? (
          <Spinner label="Loading records…" />
        ) : cell.state === 'error' ? (
          <ErrorState title="Could not load those records" description={cell.message} />
        ) : (
          <RelatedList<CellRecord>
            title={`${rowLabel(selected.row)}, ${monthLong(selected.month)}`}
            columns={cell.cell.object === 'revenue' ? revenueColumns : billingColumns}
            rows={cell.cell.records}
            getRowId={(r) => r.id}
            object={cell.cell.object}
            recordHref={recordHref}
            onNavigate={onNavigate}
            previewCount={cell.cell.records.length}
            {...(cell.cell.note ? { note: cell.cell.note } : {})}
            emptyLabel="Nothing in this month"
            compactEmpty
          />
        )
      ) : null}
    </div>
  );
}
